import { zodResolver } from '@hookform/resolvers/zod';
import {
  configMgmtControllerListDocumentTypes,
  configMgmtControllerListRequestTypes,
  documentsControllerFindAll,
  requestsControllerCreate,
  requestsControllerFindOne,
  requestsControllerUpdate,
  vehicleLicensesControllerCreate,
} from '@kalba/api-client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import {
  AlertCircle,
  Building2,
  Check,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Eye,
  FileCheck,
  FileText,
  Info,
  Loader2,
  RefreshCw,
  Save,
  Sparkles,
  Trash2,
  Upload,
  Zap,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { AppLayout } from '../../components/layout/app-layout';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { FileDropZone } from '../../components/ui/file-drop-zone';
import { DirhamIcon, Price } from '../../components/ui/price';
import { api } from '../../lib/ky';
import { cn } from '../../lib/utils';
import { type PermitFormValues, permitFormSchema } from '../../lib/validations';
import { useAuthStore } from '../../stores/auth.store';
import { type StagedDoc, usePermitFormStore } from '../../stores/permit-form.store';
import { AccountContactStep } from './components/AccountContactStep';
import { type PermitDetailsData, PermitDetailsStep } from './components/PermitDetailsStep';
import { ReviewStep } from './components/ReviewStep';

export const Route = createFileRoute('/permits/new')({
  beforeLoad: () => {
    const { user, permissions } = useAuthStore.getState();
    if (!user) throw redirect({ to: '/login' });
    if (!permissions.includes('permit:create')) throw redirect({ to: '/dashboard' });
  },
  validateSearch: z.object({
    editPermitId: z.string().optional().catch(undefined),
    accountId: z.string().optional().catch(undefined),
    accountName: z.string().optional().catch(undefined),
    contactId: z.string().optional().catch(undefined),
    contactName: z.string().optional().catch(undefined),
  }).parse,
  component: NewPermitPage,
});

// ── Types ──────────────────────────────────────────────────────────────────

interface Step1Data {
  accountId: string;
  accountName: string;
  contactId: string;
  contactName: string;
}

interface PermitTypeOption {
  id: string;
  name: string;
  code: string;
  hasRenewal: boolean;
}

const defaultStep1: Step1Data = { accountId: '', accountName: '', contactId: '', contactName: '' };

const todayIso = () => new Date().toISOString().split('T')[0];

const defaultDetails: PermitDetailsData = {
  permitTypeId: '',
  permitTypeCode: '',
  requestType: '',
  facilitySubType: '',
  riskCode: '',
  riskId: '',
  riskCategoryTotalFee: 0,
  permissionDate: todayIso(),
  expiryDate: '',
  permitActivity: '',
  envPermitGeneralComments: '',
  licenseType: '',
  isRenewalOfId: '',
  vehicleCount: 1,
  vehicleLicenses: [{ licenseNumber: '', placeOfIssue: '' }],
  wasteClassification: '',
  wasteType: '',
  wasteQuantity: '',
  landfillLocation: '',
};

// ── Details section validity (uses Zod schema as single source of truth) ──

function isDetailsSectionValid(v: PermitFormValues): boolean {
  if (!v.permitTypeCode) return false;
  const result = permitFormSchema.safeParse(v);
  if (!result.error) return true;
  const accountFields = ['accountId', 'contactId', 'accountName', 'contactName'];
  return !result.error.errors.some((e) => !accountFields.includes(String(e.path[0] ?? '')));
}

// ── Constants ──────────────────────────────────────────────────────────────

const CARD_GRADIENTS = [
  'from-green-500 to-emerald-600',
  'from-orange-500 to-amber-600',
  'from-blue-500 to-indigo-600',
  'from-purple-500 to-violet-600',
  'from-rose-500 to-pink-600',
];

const SECTION_DEFS_STATIC = [
  { id: 'account', label: 'Account & Contact', icon: Building2 },
  { id: 'type', label: 'Permit Type', icon: FileCheck },
  { id: 'details', label: 'Permit Details', icon: Sparkles },
  { id: 'docs', label: 'Documents', icon: Upload },
  { id: 'fees', label: 'Fee Summary', icon: DirhamIcon },
] as const;

// ── SectionWrap (module-level to avoid re-creation) ────────────────────────

interface SectionWrapProps {
  id: string;
  label: string;
  icon: React.ElementType;
  description: string;
  badge?: string;
  children: React.ReactNode;
  openSection: string;
  onToggle: (id: string) => void;
  completedSections: Set<string>;
}

function SectionWrap({
  id,
  label,
  icon: Icon,
  description,
  badge,
  children,
  openSection,
  onToggle,
  completedSections,
}: SectionWrapProps) {
  const isOpen = openSection === id;
  const isDone = completedSections.has(id);
  return (
    <div
      className={cn(
        'bg-white rounded-2xl border shadow-sm transition-all duration-200',
        isOpen
          ? 'border-brand-200 shadow-md shadow-brand-500/5'
          : isDone
            ? 'border-green-200'
            : 'border-gray-200',
      )}
    >
      <button
        type="button"
        onClick={() => onToggle(id)}
        className="w-full flex items-center gap-4 p-5 text-left hover:bg-gray-50/50 transition-colors"
      >
        <div
          className={cn(
            'w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-colors',
            isOpen
              ? 'bg-brand-500 text-white'
              : isDone
                ? 'bg-green-50 text-green-600'
                : 'bg-gray-100 text-gray-400',
          )}
        >
          {isDone && !isOpen ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p
              className={cn(
                'font-semibold text-[15px]',
                isOpen ? 'text-brand-600' : 'text-gray-900',
              )}
            >
              {label}
            </p>
            {badge && (
              <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-600 border border-blue-200">
                {badge}
              </span>
            )}
            {isDone && !isOpen && (
              <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-green-50 text-green-600 border border-green-200">
                Complete
              </span>
            )}
          </div>
          {!isOpen && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
        </div>
        <ChevronDown
          className={cn(
            'w-4 h-4 text-gray-400 transition-transform duration-200 shrink-0',
            isOpen && 'rotate-180',
          )}
        />
      </button>
      {isOpen && (
        <div className="px-5 pb-5 border-t border-gray-100">
          <div className="pt-5">{children}</div>
        </div>
      )}
    </div>
  );
}

// ── DocumentsSection types ─────────────────────────────────────────────────

interface DocTypeLink {
  id: string;
  documentTypeId: string;
  documentType: { id: string; name: string; code: string };
  isMandatory: boolean;
  facilitySubType?: string | null;
  status: string;
}

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

interface ExistingDoc {
  id: string;
  fileName: string;
  mimeType: string;
  fileSizeBytes: number;
  signedUrl?: string | null;
  documentType: { id: string; name: string; code: string };
}

function DocThumbnail({
  file,
  signedUrl,
  mimeType,
  onClick,
}: {
  file?: File;
  signedUrl?: string | null;
  mimeType?: string;
  onClick: () => void;
}) {
  const [objectUrl, setObjectUrl] = useState('');
  useEffect(() => {
    if (!file) return;
    const u = URL.createObjectURL(file);
    setObjectUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);

  const imgSrc = file ? objectUrl : signedUrl;
  const resolvedMime = file ? file.type : (mimeType ?? '');
  const isImg = resolvedMime.startsWith('image/');

  return (
    <button
      type="button"
      onClick={onClick}
      className="relative w-12 h-12 rounded-lg overflow-hidden border border-gray-200 bg-gray-50 shrink-0 hover:ring-2 hover:ring-brand-400 transition-all group"
      title="Preview"
    >
      {isImg && imgSrc ? (
        <img src={imgSrc} alt="" className="w-full h-full object-cover" />
      ) : (
        <div className="flex items-center justify-center w-full h-full">
          <FileText className="w-5 h-5 text-gray-400" />
        </div>
      )}
      <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
        <Eye className="w-3.5 h-3.5 text-white" />
      </div>
    </button>
  );
}

// ── DocumentsSection ───────────────────────────────────────────────────────

interface DocumentsSectionProps {
  permitTypeId: string;
  stagedDocs: StagedDoc[];
  onAdd: (doc: StagedDoc) => void;
  onRemove: (id: string) => void;
  error?: string | null;
  existingDocs?: ExistingDoc[];
}

const ALLOWED_MIME = ['application/pdf', 'image/jpeg', 'image/png'] as const;
const MAX_BYTES = 10 * 1024 * 1024;

function DocumentsSection({
  permitTypeId,
  stagedDocs,
  onAdd,
  onRemove,
  error,
  existingDocs = [],
}: DocumentsSectionProps) {
  const [uploadingDocType, setUploadingDocType] = useState<{ id: string; name: string } | null>(
    null,
  );
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState('');
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const [dropErrors, setDropErrors] = useState<Record<string, string>>({});
  const [preview, setPreview] = useState<{
    name: string;
    url: string;
    mime: string;
    objectUrl?: string;
  } | null>(null);

  const { data: docTypeLinks } = useQuery({
    queryKey: ['docTypes', permitTypeId],
    queryFn: () =>
      configMgmtControllerListDocumentTypes({ id: permitTypeId }) as unknown as Promise<
        DocTypeLink[]
      >,
    enabled: !!permitTypeId,
  });

  const filteredDocTypes = (docTypeLinks ?? []).filter(
    (l) => l.status === 'ACTIVE' && l.isMandatory,
  );

  // Paste-from-clipboard support when upload dialog is open
  useEffect(() => {
    if (!uploadingDocType) return;
    function handlePaste(e: ClipboardEvent) {
      const imageItem = Array.from(e.clipboardData?.items ?? []).find((i) =>
        i.type.startsWith('image/'),
      );
      if (imageItem) {
        const f = imageItem.getAsFile();
        if (f) {
          setFile(f);
          setFileError('');
        }
      }
    }
    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [uploadingDocType]);

  function openUpload(id: string, name: string) {
    setUploadingDocType({ id, name });
    setFile(null);
    setFileError('');
  }

  function closeDialog() {
    setUploadingDocType(null);
    setFile(null);
    setFileError('');
  }

  function confirmUpload() {
    if (!file || !uploadingDocType) return;
    const existing = stagedDocs.find((d) => d.docTypeId === uploadingDocType.id);
    if (existing) onRemove(existing.id);
    onAdd({
      id: `${Date.now()}-${Math.random()}`,
      docTypeId: uploadingDocType.id,
      docTypeName: uploadingDocType.name,
      file,
    });
    closeDialog();
  }

  function handleDrop(e: React.DragEvent<HTMLDivElement>, link: DocTypeLink) {
    e.preventDefault();
    e.stopPropagation();
    setDragOverId(null);
    const f = e.dataTransfer.files[0];
    if (!f) return;
    if (!(ALLOWED_MIME as readonly string[]).includes(f.type)) {
      setDropErrors((prev) => ({
        ...prev,
        [link.documentTypeId]: 'Only PDF, JPG, PNG files are allowed',
      }));
      setTimeout(
        () =>
          setDropErrors((prev) => {
            const n = { ...prev };
            delete n[link.documentTypeId];
            return n;
          }),
        3000,
      );
      return;
    }
    if (f.size > MAX_BYTES) {
      setDropErrors((prev) => ({ ...prev, [link.documentTypeId]: 'File must be under 10 MB' }));
      setTimeout(
        () =>
          setDropErrors((prev) => {
            const n = { ...prev };
            delete n[link.documentTypeId];
            return n;
          }),
        3000,
      );
      return;
    }
    const existingStaged = stagedDocs.find((d) => d.docTypeId === link.documentTypeId);
    if (existingStaged) onRemove(existingStaged.id);
    onAdd({
      id: `${Date.now()}-${Math.random()}`,
      docTypeId: link.documentTypeId,
      docTypeName: link.documentType.name,
      file: f,
    });
  }

  function openPreviewStaged(doc: StagedDoc) {
    const url = URL.createObjectURL(doc.file);
    setPreview({ name: doc.docTypeName, url, mime: doc.file.type, objectUrl: url });
  }

  function openPreviewExisting(doc: ExistingDoc) {
    if (!doc.signedUrl) return;
    setPreview({ name: doc.documentType.name, url: doc.signedUrl, mime: doc.mimeType });
  }

  function closePreview() {
    if (preview?.objectUrl) URL.revokeObjectURL(preview.objectUrl);
    setPreview(null);
  }

  if (!permitTypeId) {
    return (
      <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 p-4">
        <Info className="w-4 h-4 text-gray-400 shrink-0" />
        <p className="text-sm text-gray-500">
          Select a permit type first to see available document types.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Optional banner */}
      <div className="flex items-start gap-3 rounded-xl border border-blue-200 bg-blue-50 p-3.5">
        <Info className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
        <p className="text-sm text-blue-700">
          Documents are <span className="font-semibold">optional</span> at this stage. You can
          upload them now or later.
        </p>
      </div>

      {/* Document type rows */}
      {filteredDocTypes.length === 0 ? (
        <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 p-4">
          <Info className="w-4 h-4 text-gray-400 shrink-0" />
          <p className="text-sm text-gray-500">
            No document types configured for this permit type.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredDocTypes.map((link) => {
            const staged = stagedDocs.find((d) => d.docTypeId === link.documentTypeId);
            const existing = existingDocs.find((d) => d.documentType.id === link.documentTypeId);
            const hasDoc = !!(staged || existing);
            const isDragTarget = dragOverId === link.documentTypeId;
            return (
              <div key={link.documentTypeId}>
                <div
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDragOverId(link.documentTypeId);
                  }}
                  onDragLeave={(e) => {
                    if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragOverId(null);
                  }}
                  onDrop={(e) => handleDrop(e, link)}
                  className={cn(
                    'flex items-center gap-3 rounded-xl border px-4 py-3 shadow-sm transition-all',
                    isDragTarget
                      ? 'border-brand-400 bg-brand-50 ring-2 ring-brand-200'
                      : 'border-gray-200 bg-white',
                  )}
                >
                  {/* Thumbnail or icon */}
                  {hasDoc ? (
                    <DocThumbnail
                      file={staged?.file}
                      signedUrl={!staged ? existing?.signedUrl : undefined}
                      mimeType={!staged ? existing?.mimeType : undefined}
                      onClick={() => {
                        if (staged) openPreviewStaged(staged);
                        else if (existing) openPreviewExisting(existing);
                      }}
                    />
                  ) : (
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gray-100">
                      <FileText className="w-4 h-4 text-gray-400" />
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">{link.documentType.name}</p>
                    {staged && (
                      <p className="text-xs text-gray-400 truncate">
                        {staged.file.name} · {formatBytes(staged.file.size)}
                      </p>
                    )}
                    {!staged && existing && (
                      <p className="text-xs text-gray-400 truncate">
                        {existing.fileName} · {formatBytes(existing.fileSizeBytes)}
                      </p>
                    )}
                    {isDragTarget && !hasDoc && (
                      <p className="text-xs text-brand-500">Drop file here</p>
                    )}
                  </div>

                  <div className="flex items-center gap-1.5 shrink-0">
                    {hasDoc && (
                      <span className="inline-flex items-center gap-1 rounded-full border border-success-200 bg-success-50 px-2 py-0.5 text-[10px] font-semibold text-success-700">
                        <Check className="w-2.5 h-2.5" /> Uploaded
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => openUpload(link.documentTypeId, link.documentType.name)}
                      className="flex items-center justify-center w-9 h-9 rounded-xl border border-gray-200 text-gray-400 hover:border-brand-400 hover:text-brand-500 hover:bg-brand-50 transition-all"
                      title={hasDoc ? 'Replace' : 'Upload'}
                    >
                      <Upload className="w-4 h-4" />
                    </button>
                    {staged && (
                      <button
                        type="button"
                        onClick={() => onRemove(staged.id)}
                        className="flex items-center justify-center w-9 h-9 rounded-xl border border-gray-200 text-gray-400 hover:border-red-200 hover:text-red-500 hover:bg-red-50 transition-all"
                        title="Remove"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
                {dropErrors[link.documentTypeId] && (
                  <p className="text-xs text-red-500 mt-1 px-1">
                    {dropErrors[link.documentTypeId]}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 p-3">
          <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Upload dialog */}
      <Dialog
        open={!!uploadingDocType}
        onOpenChange={(open) => {
          if (!open) closeDialog();
        }}
      >
        <DialogContent className="sm:max-w-md rounded-2xl border-gray-200">
          <DialogHeader>
            <DialogTitle className="text-base">Upload Document</DialogTitle>
            <DialogDescription className="text-sm text-gray-500">
              {uploadingDocType?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-gray-400">
              Drag &amp; drop, paste from clipboard, or click to select. PDF, JPG, PNG — max 10 MB.
            </p>
            <FileDropZone
              file={file}
              onChange={setFile}
              onError={setFileError}
              accept=".pdf,.jpg,.jpeg,.png"
              allowedMime={['application/pdf', 'image/jpeg', 'image/png']}
              hint="PDF, JPG, PNG — max 10 MB"
            />
            {fileError && <p className="text-xs text-red-500">{fileError}</p>}
          </div>
          <DialogFooter className="gap-2">
            <button
              type="button"
              onClick={closeDialog}
              className="px-4 py-2 rounded-xl text-sm font-medium border border-gray-300 hover:bg-gray-50 transition-all text-gray-600"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={confirmUpload}
              disabled={!file || !!fileError}
              className={cn(
                'px-4 py-2 rounded-xl text-sm font-semibold transition-all flex items-center gap-2',
                file && !fileError
                  ? 'bg-brand-500 text-white hover:bg-brand-600 shadow-sm shadow-brand-500/25'
                  : 'bg-gray-200 text-gray-500 cursor-not-allowed',
              )}
            >
              <Upload className="w-3.5 h-3.5" /> Upload
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview dialog */}
      {preview && (
        <Dialog
          open
          onOpenChange={(open) => {
            if (!open) closePreview();
          }}
        >
          <DialogContent className="sm:max-w-3xl rounded-2xl border-gray-200">
            <DialogHeader>
              <DialogTitle className="text-base">{preview.name}</DialogTitle>
            </DialogHeader>
            <div className="overflow-auto rounded-xl max-h-[65vh]">
              {preview.mime.startsWith('image/') ? (
                <img src={preview.url} alt={preview.name} className="w-full h-auto rounded-xl" />
              ) : (
                <iframe
                  src={preview.url}
                  title={preview.name}
                  className="w-full rounded-xl border-0"
                  style={{ height: '65vh' }}
                />
              )}
            </div>
            <DialogFooter>
              <button
                type="button"
                onClick={closePreview}
                className="px-4 py-2 rounded-xl text-sm font-medium border border-gray-300 hover:bg-gray-50 transition-all text-gray-600"
              >
                Close
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// ── ContinueBtn ────────────────────────────────────────────────────────────

function ContinueBtn({ onClick, disabled }: { onClick: () => void; disabled: boolean }) {
  return (
    <div className="flex justify-end mt-5">
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={cn(
          'px-5 py-2 rounded-xl text-sm font-semibold transition-all flex items-center gap-2',
          !disabled
            ? 'bg-brand-500 text-white hover:bg-brand-600 shadow-sm shadow-brand-500/25'
            : 'bg-gray-200 text-gray-500 cursor-not-allowed',
        )}
      >
        Continue <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────

function NewPermitPage() {
  const { editPermitId, accountId, accountName, contactId, contactName } = Route.useSearch();
  const prefillAccount = accountId
    ? {
        accountId,
        accountName: accountName ?? '',
        contactId: contactId ?? '',
        contactName: contactName ?? '',
      }
    : undefined;
  return (
    <AppLayout noPadding>
      <NewPermitContent editPermitId={editPermitId} prefillAccount={prefillAccount} />
    </AppLayout>
  );
}

interface ExistingPermitData {
  id: string;
  referenceNo: string;
  requestTypeId: string;
  requestTypeConfig: { id: string; name: string; code: string };
  requestAction: string;
  facilitySubType?: string | null;
  riskCode?: string | null;
  licenseType?: string | null;
  permitActivity?: string | null;
  envPermitGeneralComments?: string | null;
  permissionDate?: string | null;
  expiryDate?: string | null;
  isRenewalOfId?: string | null;
  wasteClassification?: string | null;
  wasteType?: string | null;
  wasteQuantity?: string | null;
  landfillLocation?: string | null;
  account: { id: string; name: string };
  contact: { id: string; fullName: string };
  vehicleLicenses: { licenseNumber: string; placeOfIssue: string }[];
}

function NewPermitContent({
  editPermitId,
  prefillAccount,
}: { editPermitId?: string; prefillAccount?: Step1Data }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isEditMode = !!editPermitId;
  const initialized = useRef(false);

  const {
    openSection,
    completedSections: completedSectionsArr,
    stagedDocs,
    docsError,
    setOpenSection,
    addCompletedSection,
    setCompletedSections,
    addStagedDoc,
    removeStagedDoc,
    setDocsError,
    reset: resetStore,
  } = usePermitFormStore();

  const completedSections = new Set(completedSectionsArr);

  const form = useForm<PermitFormValues>({
    resolver: zodResolver(permitFormSchema),
    defaultValues: {
      ...defaultStep1,
      ...defaultDetails,
      ...(prefillAccount?.accountId ? prefillAccount : {}),
    },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const fv = form.watch();

  const step1: Step1Data = {
    accountId: fv.accountId,
    accountName: fv.accountName,
    contactId: fv.contactId,
    contactName: fv.contactName,
  };

  const details: PermitDetailsData = {
    permitTypeId: fv.permitTypeId,
    permitTypeCode: fv.permitTypeCode,
    requestType: fv.requestType,
    facilitySubType: fv.facilitySubType,
    riskCode: fv.riskCode,
    riskId: fv.riskId,
    riskCategoryTotalFee: fv.riskCategoryTotalFee,
    permissionDate: fv.permissionDate,
    expiryDate: fv.expiryDate,
    permitActivity: fv.permitActivity,
    envPermitGeneralComments: fv.envPermitGeneralComments,
    licenseType: fv.licenseType,
    isRenewalOfId: fv.isRenewalOfId,
    vehicleCount: fv.vehicleCount,
    vehicleLicenses: fv.vehicleLicenses,
    wasteClassification: fv.wasteClassification,
    wasteType: fv.wasteType,
    wasteQuantity: fv.wasteQuantity,
    landfillLocation: fv.landfillLocation,
  };

  const fe = form.formState.errors;
  const step1Errors: Partial<Record<keyof Step1Data, string>> = {
    accountId: fe.accountId?.message,
    contactId: fe.contactId?.message,
  };
  const detailsErrors: Partial<Record<keyof PermitDetailsData, string>> = {
    permitTypeCode: fe.permitTypeCode?.message,
    requestType: fe.requestType?.message,
    riskCode: fe.riskCode?.message,
    permissionDate: fe.permissionDate?.message,
    expiryDate: fe.expiryDate?.message,
    wasteQuantity: fe.wasteQuantity?.message,
    vehicleLicenses: (fe.vehicleLicenses as { message?: string } | undefined)?.message,
  };

  function handleStep1Change(updates: Partial<Step1Data>) {
    (Object.entries(updates) as [keyof Step1Data, string][]).forEach(([k, v]) =>
      form.setValue(k, v),
    );
  }

  function handleDetailsChange(updates: Partial<PermitDetailsData>) {
    (
      Object.entries(updates) as [
        keyof PermitDetailsData,
        PermitDetailsData[keyof PermitDetailsData],
      ][]
    ).forEach(([k, v]) => form.setValue(k as keyof PermitFormValues, v as never));
  }

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [autoSaveTime, setAutoSaveTime] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // permit types for inline type cards — PERMIT domain only (excludes SM_STUDY / Sharjah Municipality)
  const { data: permitTypes } = useQuery({
    queryKey: ['permit-types', 'PERMIT'],
    queryFn: () =>
      configMgmtControllerListRequestTypes({ domain: 'PERMIT' }) as unknown as Promise<
        PermitTypeOption[]
      >,
  });

  // fetch existing permit when in edit mode
  const { data: existingPermit } = useQuery({
    queryKey: ['permit', editPermitId],
    queryFn: () =>
      requestsControllerFindOne({ id: editPermitId! }) as unknown as Promise<ExistingPermitData>,
    enabled: isEditMode,
  });

  // fetch already-uploaded documents when editing so they appear as thumbnails
  const { data: existingDocs } = useQuery({
    queryKey: ['permitDocs', editPermitId],
    queryFn: () =>
      documentsControllerFindAll({ permitId: editPermitId! }) as unknown as Promise<ExistingDoc[]>,
    enabled: isEditMode && !!editPermitId,
  });

  // pre-fill form once existing permit data loads
  useEffect(() => {
    if (!existingPermit || initialized.current) return;
    initialized.current = true;

    const vLicenses = existingPermit.vehicleLicenses?.length
      ? existingPermit.vehicleLicenses.map((l) => ({
          licenseNumber: l.licenseNumber,
          placeOfIssue: l.placeOfIssue,
        }))
      : [{ licenseNumber: '', placeOfIssue: '' }];

    form.reset({
      accountId: existingPermit.account.id,
      accountName: existingPermit.account.name,
      contactId: existingPermit.contact.id,
      contactName: existingPermit.contact.fullName,
      permitTypeId: existingPermit.requestTypeId,
      permitTypeCode: existingPermit.requestTypeConfig.code,
      requestType: existingPermit.requestAction ?? '',
      facilitySubType: existingPermit.facilitySubType ?? '',
      riskCode: existingPermit.riskCode ?? '',
      riskId: '',
      riskCategoryTotalFee: 0,
      permissionDate: existingPermit.permissionDate
        ? existingPermit.permissionDate.split('T')[0]
        : todayIso(),
      expiryDate: existingPermit.expiryDate ? existingPermit.expiryDate.split('T')[0] : '',
      permitActivity: existingPermit.permitActivity ?? '',
      envPermitGeneralComments: existingPermit.envPermitGeneralComments ?? '',
      licenseType: existingPermit.licenseType ?? '',
      isRenewalOfId: existingPermit.isRenewalOfId ?? '',
      vehicleCount: vLicenses.length,
      vehicleLicenses: vLicenses,
      wasteClassification: existingPermit.wasteClassification ?? '',
      wasteType: existingPermit.wasteType ?? '',
      wasteQuantity: existingPermit.wasteQuantity ?? '',
      landfillLocation: existingPermit.landfillLocation ?? '',
    });

    setCompletedSections(['account', 'type', 'details', 'docs', 'fees']);
  }, [existingPermit]);

  // Pre-fill account section from URL params (e.g. navigating from customer detail)
  useEffect(() => {
    if (!prefillAccount?.accountId || isEditMode) return;
    if (prefillAccount.contactId) {
      addCompletedSection('account');
      setOpenSection('type');
    } else {
      setOpenSection('account');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset store on unmount so state doesn't bleed into next navigation
  useEffect(() => {
    return () => resetStore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // auto-save simulation
  useEffect(() => {
    const t = setTimeout(() => {
      if (fv.accountId || fv.permitTypeCode) {
        const now = new Date();
        setAutoSaveTime(
          `${now.getHours()}:${String(now.getMinutes()).padStart(2, '0')} ${now.getHours() >= 12 ? 'PM' : 'AM'}`,
        );
      }
    }, 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fv.accountId, fv.permitTypeCode]);

  const sectionValid = {
    account: !!(step1.accountId && step1.contactId),
    type: !!details.permitTypeCode,
    details: isDetailsSectionValid(fv),
    fees: true,
    docs: stagedDocs.length > 0,
  };
  // docs section is optional for progress — count only the 4 required sections
  const requiredValid = {
    account: sectionValid.account,
    type: sectionValid.type,
    details: sectionValid.details,
    fees: sectionValid.fees,
  };
  const filledCount = Object.values(requiredValid).filter(Boolean).length;
  const overallProgress = Math.round(((filledCount + (sectionValid.docs ? 1 : 0)) / 5) * 100);

  function toggleSection(id: string) {
    setOpenSection(openSection === id ? '' : id);
  }

  function completeSection(id: string, next?: string) {
    addCompletedSection(id);
    setOpenSection(next ?? '');
  }

  const onFormValid = form.handleSubmit(
    async (data) => {
      setDocsError(null);
      setSubmitError(null);
      setSubmitting(true);
      try {
        const created = await (requestsControllerCreate({
          requestBody: {
            accountId: data.accountId,
            contactId: data.contactId,
            requestTypeId: data.permitTypeId,
            requestAction: (data.requestType || undefined) as 'NEW' | 'RENEWAL' | undefined,
            facilitySubType: (data.facilitySubType || undefined) as
              | 'NEW_FACILITY'
              | 'EXISTING_FACILITY'
              | undefined,
            riskCode: (data.riskCode || undefined) as never,
            permissionDate: data.permissionDate || undefined,
            expiryDate: data.expiryDate || undefined,
            permitActivity: data.permitActivity || undefined,
            envPermitGeneralComments: data.envPermitGeneralComments || undefined,
            licenseType: data.licenseType || undefined,
            isRenewalOfId: data.isRenewalOfId || undefined,
            wasteClassification: data.wasteClassification || undefined,
            wasteType: data.wasteType || undefined,
            wasteQuantity: data.wasteQuantity ? Number(data.wasteQuantity) : undefined,
            landfillLocation: data.landfillLocation || undefined,
          },
        }) as unknown as Promise<{ id: string }>);

        if (data.permitTypeCode === 'VEH') {
          for (const lic of data.vehicleLicenses.filter((l) => l.licenseNumber.trim())) {
            await vehicleLicensesControllerCreate({
              permitId: created.id,
              requestBody: { licenseNumber: lic.licenseNumber, placeOfIssue: lic.placeOfIssue },
            });
          }
        }

        for (const doc of stagedDocs) {
          const fd = new FormData();
          fd.append('file', doc.file);
          fd.append('documentTypeId', doc.docTypeId);
          await api.post(`permits/${created.id}/documents`, { body: fd }).json();
        }

        navigate({ to: '/permits/$permitId', params: { permitId: created.id } });
      } catch (err: unknown) {
        const body = (err as { body?: { message?: string } })?.body;
        setSubmitError(
          body?.message ??
            (err instanceof Error
              ? err.message
              : 'An unexpected error occurred. Please try again.'),
        );
      } finally {
        setSubmitting(false);
      }
    },
    (errors) => {
      if (errors.accountId || errors.contactId) setOpenSection('account');
      else if (errors.permitTypeCode) setOpenSection('type');
      else setOpenSection('details');
    },
  );

  async function handleSubmit() {
    if (isEditMode) {
      setSubmitError(null);
      setSubmitting(true);
      try {
        const data = form.getValues();
        await requestsControllerUpdate({
          id: editPermitId!,
          requestBody: {
            contactId: data.contactId || undefined,
            facilitySubType: (data.facilitySubType || undefined) as
              | 'NEW_FACILITY'
              | 'EXISTING_FACILITY'
              | undefined,
            riskCode: data.riskCode || undefined,
            permissionDate: data.permissionDate || undefined,
            expiryDate: data.expiryDate || undefined,
            permitActivity: data.permitActivity || undefined,
            envPermitGeneralComments: data.envPermitGeneralComments || undefined,
            licenseType: data.licenseType || undefined,
            wasteClassification: data.wasteClassification || undefined,
            wasteType: data.wasteType || undefined,
            wasteQuantity: data.wasteQuantity ? Number(data.wasteQuantity) : undefined,
            landfillLocation: data.landfillLocation || undefined,
          },
        });
        for (const doc of stagedDocs) {
          const fd = new FormData();
          fd.append('file', doc.file);
          fd.append('documentTypeId', doc.docTypeId);
          await api.post(`permits/${editPermitId}/documents`, { body: fd }).json();
        }
        await qc.invalidateQueries({ queryKey: ['permit', editPermitId] });
        navigate({ to: '/permits/$permitId', params: { permitId: editPermitId! } });
      } catch (err: unknown) {
        const body = (err as { body?: { message?: string } })?.body;
        setSubmitError(
          body?.message ??
            (err instanceof Error
              ? err.message
              : 'An unexpected error occurred. Please try again.'),
        );
      } finally {
        setSubmitting(false);
      }
      return;
    }

    await onFormValid();
  }

  return (
    <>
      <div className="flex flex-col min-h-[calc(100vh-4rem)]">
        {/* ── Hero header ──────────────────────────────────────────────────── */}
        <div className="bg-gradient-to-br from-white via-white to-gray-50/80 border-b border-gray-200 px-6 py-5 lg:px-8">
          {/* Breadcrumb */}
          <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-4">
            <button
              type="button"
              onClick={() => navigate({ to: '/permits', search: { page: 1, limit: 20 } })}
              className="hover:text-gray-800 transition-colors"
            >
              Permit Requests
            </button>
            {isEditMode && editPermitId && (
              <>
                <ChevronRight className="w-3 h-3" />
                <button
                  type="button"
                  onClick={() =>
                    navigate({ to: '/permits/$permitId', params: { permitId: editPermitId } })
                  }
                  className="hover:text-gray-800 transition-colors"
                >
                  {existingPermit?.referenceNo ?? editPermitId}
                </button>
              </>
            )}
            <ChevronRight className="w-3 h-3" />
            <span className="text-gray-800 font-medium">
              {isEditMode ? 'Edit Permit' : 'New Permit Request'}
            </span>
          </div>

          <div className="flex items-start justify-between gap-4">
            <div className="space-y-2">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-2xl font-bold tracking-tight text-gray-900">
                  {isEditMode
                    ? `Edit Permit${existingPermit ? ` — ${existingPermit.referenceNo}` : ''}`
                    : 'New Permit Request'}
                </h1>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-warning-50 text-warning-600 border border-warning-200">
                  <span className="w-1.5 h-1.5 rounded-full bg-warning-500" />
                  Draft
                </span>
                {autoSaveTime && (
                  <span className="inline-flex items-center gap-1.5 text-xs text-gray-400">
                    <RefreshCw className="w-3 h-3" /> Auto-saved at {autoSaveTime}
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-500">
                Create and submit permit applications with real-time fee calculation.
              </p>
              <div className="flex items-center gap-3 pt-1">
                <div className="w-48 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-brand-500 rounded-full transition-all duration-500"
                    style={{ width: `${overallProgress}%` }}
                  />
                </div>
                <span className="text-xs font-medium text-gray-500">
                  {filledCount} of 4 sections complete
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Two-column body ───────────────────────────────────────────────── */}
        <div className="flex flex-1">
          {/* Left: accordion sections */}
          <div className="flex-1 min-w-0 px-6 py-6 pb-24 space-y-3 lg:px-8">
            {/* Section 1: Account & Contact */}
            <SectionWrap
              id="account"
              label="Account & Contact"
              icon={Building2}
              description="Link this permit to a customer account and contact person"
              openSection={openSection}
              onToggle={toggleSection}
              completedSections={completedSections}
            >
              <AccountContactStep value={step1} onChange={handleStep1Change} errors={step1Errors} />
              <ContinueBtn
                onClick={async () => {
                  const valid = await form.trigger(['accountId', 'contactId']);
                  if (!valid) return;
                  completeSection('account', 'type');
                }}
                disabled={!sectionValid.account}
              />
            </SectionWrap>

            {/* Section 2: Permit Type */}
            <SectionWrap
              id="type"
              label="Permit Type"
              icon={FileCheck}
              description="Choose the category that best fits your project"
              openSection={openSection}
              onToggle={toggleSection}
              completedSections={completedSections}
            >
              {isEditMode && (
                <div className="flex items-center gap-2 mb-3 rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-2.5">
                  <Info className="w-4 h-4 text-amber-500 shrink-0" />
                  <p className="text-xs text-amber-700">
                    Permit type cannot be changed after creation.
                  </p>
                </div>
              )}
              <div
                className={cn(
                  'grid gap-4',
                  permitTypes && permitTypes.length <= 3
                    ? 'grid-cols-3'
                    : 'grid-cols-2 sm:grid-cols-3',
                )}
              >
                {(permitTypes ?? []).map((pt, i) => {
                  const isSelected = details.permitTypeCode === pt.code;
                  const gradient = CARD_GRADIENTS[i % CARD_GRADIENTS.length];
                  return (
                    <button
                      key={pt.code}
                      type="button"
                      disabled={isEditMode && !isSelected}
                      onClick={() => {
                        if (isEditMode) return;
                        const s1 = form.getValues([
                          'accountId',
                          'accountName',
                          'contactId',
                          'contactName',
                        ]);
                        form.reset({
                          accountId: s1[0],
                          accountName: s1[1],
                          contactId: s1[2],
                          contactName: s1[3],
                          ...defaultDetails,
                          permitTypeCode: pt.code,
                          permitTypeId: pt.id,
                        });
                      }}
                      className={cn(
                        'relative p-5 rounded-2xl border-2 text-left transition-all duration-200',
                        isSelected
                          ? 'border-brand-500 bg-brand-50 shadow-lg shadow-brand-500/10'
                          : isEditMode
                            ? 'border-gray-100 bg-gray-50 opacity-40 cursor-not-allowed'
                            : 'border-gray-200 hover:border-brand-200 hover:shadow-md bg-white',
                      )}
                    >
                      {isSelected && (
                        <div className="absolute top-3 right-3 w-5 h-5 rounded-full bg-brand-500 flex items-center justify-center">
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      )}
                      <div
                        className={cn(
                          'w-12 h-12 rounded-xl bg-gradient-to-br flex items-center justify-center mb-4 shadow-md',
                          gradient,
                        )}
                      >
                        <FileCheck className="w-6 h-6 text-white" />
                      </div>
                      <p className="font-semibold text-[15px] mb-1.5 text-gray-900">{pt.name}</p>
                      {pt.hasRenewal && (
                        <p className="text-xs text-gray-500 leading-relaxed">New &amp; Renewal</p>
                      )}
                    </button>
                  );
                })}
              </div>
              {detailsErrors.permitTypeCode && (
                <p className="mt-2 text-xs text-red-500">{detailsErrors.permitTypeCode}</p>
              )}
              <ContinueBtn
                onClick={async () => {
                  const valid = await form.trigger(['permitTypeCode']);
                  if (!valid) return;
                  completeSection('type', 'details');
                }}
                disabled={!sectionValid.type}
              />
            </SectionWrap>

            {/* Section 3: Permit Details (type-specific fields) */}
            <SectionWrap
              id="details"
              label="Permit Details"
              icon={Sparkles}
              description={
                details.permitTypeCode
                  ? `Specific fields for your ${permitTypes?.find((t) => t.code === details.permitTypeCode)?.name ?? details.permitTypeCode} permit`
                  : 'Select a permit type first'
              }
              badge={
                details.permitTypeCode
                  ? (permitTypes?.find((t) => t.code === details.permitTypeCode)?.name ??
                    details.permitTypeCode)
                  : undefined
              }
              openSection={openSection}
              onToggle={toggleSection}
              completedSections={completedSections}
            >
              {!details.permitTypeCode ? (
                <div className="flex items-center gap-3 p-4 bg-warning-50 border border-warning-200 rounded-xl">
                  <Info className="w-5 h-5 text-warning-600 shrink-0" />
                  <p className="text-sm text-gray-500">
                    Please select a permit type in Section 2 to see the relevant fields.
                  </p>
                </div>
              ) : (
                <>
                  <PermitDetailsStep
                    value={details}
                    onChange={handleDetailsChange}
                    errors={detailsErrors}
                    accountId={step1.accountId}
                    hideTypeCards
                  />
                  <ContinueBtn
                    onClick={async () => {
                      const valid = await form.trigger([
                        'requestType',
                        'riskCode',
                        'permissionDate',
                        'expiryDate',
                        'wasteQuantity',
                        'vehicleLicenses',
                      ]);
                      if (!valid) return;
                      completeSection('details', 'docs');
                    }}
                    disabled={!sectionValid.details}
                  />
                </>
              )}
            </SectionWrap>

            {/* Section 4: Documents */}
            <SectionWrap
              id="docs"
              label="Documents"
              icon={Upload}
              description={
                stagedDocs.length > 0
                  ? `${stagedDocs.length} document${stagedDocs.length !== 1 ? 's' : ''} added`
                  : 'Upload supporting documents for your permit application'
              }
              openSection={openSection}
              onToggle={toggleSection}
              completedSections={completedSections}
            >
              <DocumentsSection
                permitTypeId={details.permitTypeId}
                stagedDocs={stagedDocs}
                onAdd={(doc) => {
                  addStagedDoc(doc);
                  setDocsError(null);
                  addCompletedSection('docs');
                }}
                onRemove={removeStagedDoc}
                error={docsError}
                existingDocs={existingDocs}
              />
              <ContinueBtn onClick={() => completeSection('docs', 'fees')} disabled={false} />
            </SectionWrap>

            {/* Section 5: Fee Summary */}
            <SectionWrap
              id="fees"
              label="Fee Summary"
              icon={DirhamIcon}
              description="Estimated fees are auto-calculated based on your selections"
              openSection={openSection}
              onToggle={toggleSection}
              completedSections={completedSections}
            >
              {!details.permitTypeId ||
              (!details.requestType && details.permitTypeCode !== 'WASTE') ? (
                <div className="flex items-center gap-3 p-4 bg-gray-50 border border-gray-200 rounded-xl">
                  <Info className="w-4 h-4 text-gray-400 shrink-0" />
                  <p className="text-sm text-gray-500">
                    Complete permit type and details to see fee estimates.
                  </p>
                </div>
              ) : (
                <>
                  <ReviewStep
                    data={{
                      accountName: step1.accountName,
                      contactName: step1.contactName,
                      details,
                    }}
                  />
                  <div className="flex justify-end mt-5">
                    <button
                      type="button"
                      onClick={() => completeSection('fees')}
                      className="px-5 py-2 rounded-xl text-sm font-semibold bg-brand-500 text-white hover:bg-brand-600 transition-all flex items-center gap-2 shadow-sm shadow-brand-500/25"
                    >
                      <Check className="w-4 h-4" /> Mark as Reviewed
                    </button>
                  </div>
                </>
              )}
            </SectionWrap>
          </div>

          {/* Right: Application Summary panel */}
          <div className="w-80 shrink-0 px-6 py-6 hidden lg:block">
            <div className="sticky top-20 space-y-4">
              {/* Main summary card */}
              <div className="bg-white rounded-2xl border border-gray-200 shadow-md overflow-hidden">
                <div className="p-5 border-b border-gray-100">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-6 h-6 rounded-lg bg-brand-50 flex items-center justify-center">
                      <Zap className="w-3.5 h-3.5 text-brand-500" />
                    </div>
                    <p className="font-semibold text-sm text-gray-900">Application Summary</p>
                  </div>
                  <p className="text-xs text-gray-500">Live preview of your permit request</p>
                </div>

                <div className="p-5 space-y-4">
                  {/* Circular progress */}
                  <div className="flex items-center gap-3">
                    <div className="relative w-12 h-12 shrink-0">
                      <svg className="w-12 h-12 -rotate-90" viewBox="0 0 48 48">
                        <circle
                          cx="24"
                          cy="24"
                          r="20"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="4"
                          className="text-gray-200"
                        />
                        <circle
                          cx="24"
                          cy="24"
                          r="20"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="4"
                          className="text-brand-500"
                          strokeDasharray={`${2 * Math.PI * 20}`}
                          strokeDashoffset={`${2 * Math.PI * 20 * (1 - overallProgress / 100)}`}
                          strokeLinecap="round"
                          style={{ transition: 'stroke-dashoffset 0.5s ease' }}
                        />
                      </svg>
                      <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-gray-900">
                        {overallProgress}%
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">
                        {isEditMode
                          ? 'Edit Mode'
                          : filledCount < 4
                            ? 'In Progress'
                            : 'Ready to Submit'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {isEditMode
                          ? 'Modify fields and save'
                          : `${4 - filledCount} section${4 - filledCount !== 1 ? 's' : ''} remaining`}
                      </p>
                    </div>
                  </div>

                  {/* Section checklist */}
                  <div className="space-y-1.5">
                    {SECTION_DEFS_STATIC.map((s) => {
                      const isDone = completedSections.has(s.id);
                      const isValid = sectionValid[s.id as keyof typeof sectionValid] ?? false;
                      return (
                        <div
                          key={s.id}
                          onClick={() => setOpenSection(s.id)}
                          className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-gray-50 cursor-pointer transition-colors group"
                        >
                          <div
                            className={cn(
                              'w-5 h-5 rounded-full flex items-center justify-center shrink-0',
                              isDone ? 'bg-green-500' : isValid ? 'bg-brand-100' : 'bg-gray-200',
                            )}
                          >
                            {isDone ? (
                              <Check className="w-3 h-3 text-white" />
                            ) : isValid ? (
                              <div className="w-2 h-2 rounded-full bg-brand-500" />
                            ) : (
                              <div className="w-2 h-2 rounded-full bg-gray-400" />
                            )}
                          </div>
                          <span
                            className={cn(
                              'text-xs font-medium flex-1',
                              isDone ? 'text-gray-900' : 'text-gray-500',
                            )}
                          >
                            {s.label}
                          </span>
                          <ChevronRight className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      );
                    })}
                  </div>

                  {/* Live summary data */}
                  {(step1.accountName || details.permitTypeCode) && (
                    <div className="pt-3 border-t border-gray-100 space-y-2">
                      {step1.accountName && (
                        <div className="flex justify-between gap-2">
                          <span className="text-xs text-gray-400 shrink-0">Account</span>
                          <span className="text-xs font-semibold text-right truncate max-w-[160px]">
                            {step1.accountName}
                          </span>
                        </div>
                      )}
                      {step1.contactName && (
                        <div className="flex justify-between gap-2">
                          <span className="text-xs text-gray-400 shrink-0">Contact</span>
                          <span className="text-xs font-semibold text-right truncate max-w-[160px]">
                            {step1.contactName}
                          </span>
                        </div>
                      )}
                      {details.permitTypeCode && (
                        <div className="flex justify-between gap-2">
                          <span className="text-xs text-gray-400 shrink-0">Permit Type</span>
                          <span className="text-xs font-semibold">
                            {permitTypes?.find((t) => t.code === details.permitTypeCode)?.name ??
                              details.permitTypeCode}
                          </span>
                        </div>
                      )}
                      {!!details.riskId && (
                        <div className="flex justify-between gap-2 pt-1 border-t border-gray-100 mt-1">
                          <span className="text-xs text-gray-400 shrink-0">Total Cost</span>
                          <Price
                            value={details.riskCategoryTotalFee}
                            className="text-xs font-semibold text-brand-600"
                            iconClassName="text-brand-500 w-[0.85em] h-[0.85em]"
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Missing information */}
                {filledCount < 4 && (
                  <div className="px-5 pb-4">
                    <div className="p-3 bg-warning-50 border border-warning-200 rounded-xl">
                      <p className="text-xs font-semibold text-warning-600 mb-2">
                        Missing Information
                      </p>
                      <ul className="space-y-1">
                        {!sectionValid.account && (
                          <li className="text-xs text-gray-500 flex items-center gap-1.5">
                            <span className="w-1 h-1 rounded-full bg-warning-500 shrink-0" />
                            Account &amp; Contact
                          </li>
                        )}
                        {!sectionValid.type && (
                          <li className="text-xs text-gray-500 flex items-center gap-1.5">
                            <span className="w-1 h-1 rounded-full bg-warning-500 shrink-0" />
                            Permit type selection
                          </li>
                        )}
                        {!sectionValid.details && details.permitTypeCode && (
                          <li className="text-xs text-gray-500 flex items-center gap-1.5">
                            <span className="w-1 h-1 rounded-full bg-warning-500 shrink-0" />
                            Permit-specific details
                          </li>
                        )}
                      </ul>
                    </div>
                  </div>
                )}

                <div className="px-5 pb-5 space-y-2">
                  <button
                    type="button"
                    disabled={(filledCount < 3 && !isEditMode) || submitting}
                    onClick={handleSubmit}
                    className={cn(
                      'w-full py-3 rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-2',
                      isEditMode || filledCount >= 3
                        ? 'bg-brand-500 text-white hover:bg-brand-600 shadow-lg shadow-brand-500/20'
                        : 'bg-gray-200 text-gray-500 cursor-not-allowed',
                    )}
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" /> Saving…
                      </>
                    ) : isEditMode || filledCount >= 3 ? (
                      <>
                        <Save className="w-4 h-4" /> {isEditMode ? 'Save Changes' : 'Save'}
                      </>
                    ) : (
                      `Complete ${4 - filledCount} more section${4 - filledCount !== 1 ? 's' : ''}`
                    )}
                  </button>
                </div>
              </div>

              {/* Help card */}
              <div className="bg-white rounded-2xl border border-gray-200 p-4 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <Info className="w-4 h-4 text-brand-500" />
                  <p className="text-xs font-semibold text-gray-900">Need help?</p>
                </div>
                <p className="text-xs text-gray-500 leading-relaxed">
                  Our team reviews all applications within 2–3 business days. Ensure all required
                  fields are complete before submitting.
                </p>
                <button
                  type="button"
                  className="mt-3 text-xs font-semibold text-brand-500 hover:text-brand-600 transition-colors flex items-center gap-1"
                >
                  View permit guidelines <ExternalLink className="w-3 h-3" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Submit error banner ──────────────────────────────────────────── */}
      {submitError && (
        <div className="fixed bottom-4 right-4 z-30 flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 shadow-lg max-w-sm">
          <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />
          <p className="text-sm text-red-700 flex-1">{submitError}</p>
          <button
            type="button"
            onClick={() => setSubmitError(null)}
            className="text-red-400 hover:text-red-600 text-xs font-medium"
          >
            Dismiss
          </button>
        </div>
      )}
    </>
  );
}
